// Turns the host's one-time exchange code into a Nakama session.
//
// The exchange itself is SERVER-TO-SERVER: `partner_session_exchange` in
// game-server-go posts the code to that partner's validation endpoint with its
// own credentials, gets back an opaque partner user id, and mints a Nakama
// session for `custom_id = "<partner>:<partnerUserId>"`. This client only
// carries the code across and receives the resulting tokens.
//
// The partner is a PARAMETER throughout, never a constant: one deployment of
// this renderer serves every host, and which one is asking comes from the
// launch URL.
//
// Consequences worth holding onto:
//   - No partner PII ever reaches this bundle. The server rejects a validator
//     response that looks like it contains a phone number or an email, so
//     there is nothing here to leak even by accident.
//   - The code is single-use. A WebView reload cannot re-run the exchange with
//     the same code — see `cacheSession`/`restoreSession` for why a reload
//     still lands the player back in their match.
//   - The minted account is namespaced. Every economy RPC on the server
//     refuses a caller whose custom id starts with `shuttlers:`, so a
//     compromised web build cannot mint currency.

import { Client, Session } from '@heroiclabs/nakama-js';
import {
  nakamaHost,
  nakamaHttpKey,
  nakamaClientPort,
  nakamaServerKey,
  nakamaUseSsl,
  partnerSessionExchangeRpc,
} from '../config';

export interface ExchangeResult {
  readonly session: Session;
  /** True when the exchange created the Nakama account rather than reusing it. */
  readonly created: boolean;
}

export type ExchangeFailure =
  /** No code in the launch fragment — opened outside the host app. */
  | 'no_code'
  /** The partner declined THIS code: expired, already spent, wrong session. */
  | 'rejected'
  /** Too many attempts for this rider in the rate-limit window. */
  | 'rate_limited'
  /** The exchange is not deployed or not provisioned on this server. */
  | 'unavailable'
  /** Could not reach the server at all, or it failed. Retryable. */
  | 'network'
  /** Reached it, but the response was unusable. */
  | 'malformed';

export class SessionExchangeError extends Error {
  constructor(
    message: string,
    readonly reason: ExchangeFailure,
  ) {
    super(message);
    this.name = 'SessionExchangeError';
  }
}

/**
 * Maps a Nakama RPC HTTP status onto what the rider should actually do.
 *
 * WHY THIS IS NOT "4xx means the code was rejected": a 404 means the RPC does
 * not exist on this server — the exchange has not been deployed, or the host
 * is pointed at the wrong one. Reporting that as "your sign-in link expired"
 * sends the rider into a retry loop that can never succeed, and hides a
 * deployment fault from whoever is debugging it. That is exactly the state a
 * partner integration is in on day one, so it is the message QA sees first.
 *
 * The statuses below are the ones Nakama maps our own RPC's gRPC codes onto:
 *
 *   401  UNAUTHENTICATED     — the partner declined the code. The ONLY case
 *                              where "ask the app for a fresh one" is right.
 *   429  RESOURCE_EXHAUSTED  — our per-rider rate limit.
 *   404  (no such RPC)       — not deployed here.
 *   400  INVALID_ARGUMENT    — unknown partner, or a malformed payload. A
 *                              configuration fault, never the rider's doing.
 *   503  UNAVAILABLE         — provisioned but the partner could not be
 *                              reached. Retryable.
 */
export function reasonForStatus(status: number | undefined): ExchangeFailure {
  switch (status) {
    case 401:
      return 'rejected';
    case 429:
      return 'rate_limited';
    case 404:
    case 400:
    case 403:
    case 501:
      return 'unavailable';
    default:
      // 5xx, a transport failure, and anything unrecognised. Retryable, which
      // is the safe default: telling someone to retry a permanent failure
      // wastes a tap, telling them a transient one is permanent loses them.
      return 'network';
  }
}

let client: Client | null = null;

/** The shared Nakama HTTP client. One per document. */
export function nakamaClient(): Client {
  client ??= new Client(nakamaServerKey, nakamaHost, nakamaClientPort, nakamaUseSsl);
  return client;
}

/**
 * Exchanges [code] for a Nakama session.
 *
 * Called exactly once per document load. The caller is responsible for
 * deciding what to do when it throws — which, inside a host app, means
 * rendering a retry affordance rather than a blank WebView.
 */
export async function exchangePartnerCode(
  partnerSlug: string,
  code: string | null,
  opts: { trip?: string | null; route?: string | null } = {},
): Promise<ExchangeResult> {
  if (!code) {
    throw new SessionExchangeError(
      'No exchange code in the launch fragment.',
      'no_code',
    );
  }

  let raw: unknown;
  try {
    const response = await nakamaClient().rpcHttpKey(
      nakamaHttpKey,
      partnerSessionExchangeRpc,
      {
        partner: partnerSlug,
        code,
        ...(opts.trip ? { trip: opts.trip } : {}),
        ...(opts.route ? { route: opts.route } : {}),
      },
    );
    raw = response.payload;
  } catch (err) {
    const status = (err as { status?: number })?.status;
    throw new SessionExchangeError(
      `partner_session_exchange failed (status ${status ?? 'unknown'}).`,
      reasonForStatus(status),
    );
  }

  const payload = typeof raw === 'string' ? safeParse(raw) : raw;
  const token = readString(payload, 'token');
  const refresh = readString(payload, 'refresh');
  if (!token || !refresh) {
    throw new SessionExchangeError(
      'partner_session_exchange returned no session tokens.',
      'malformed',
    );
  }

  return {
    session: Session.restore(token, refresh),
    created: readBool(payload, 'created'),
  };
}

/**
 * Refreshes an expiring session.
 *
 * Distinct from the exchange: the one-time code is long gone by the time a
 * token needs rotating, so a session that cannot refresh means the player has
 * to be handed back to the host for a fresh launch, not retried here.
 */
export async function refreshSession(session: Session): Promise<Session> {
  return nakamaClient().sessionRefresh(session);
}

/**
 * Session storage across a WebView reload.
 *
 * The host may reload this document (rotation, an OS memory reclaim, a manual
 * retry) and the launch code will already have been spent. Caching the minted
 * tokens in `sessionStorage` — which dies with the WebView, unlike
 * `localStorage` — lets a reload rejoin the match instead of dead-ending on a
 * used code. It is a bearer token in web storage, so: same-origin only, never
 * written to `localStorage`, and cleared the moment the player exits.
 */
function cacheKey(partnerSlug: string): string {
  // Keyed by PARTNER. Two partners will never share an origin today, but a
  // shared one would otherwise let a reload restore the wrong company's
  // session — a failure that would look like a mysterious identity mix-up
  // rather than a caching bug.
  return `nh.session.${partnerSlug}`;
}

export function cacheSession(partnerSlug: string, session: Session): void {
  try {
    window.sessionStorage.setItem(
      cacheKey(partnerSlug),
      JSON.stringify({ token: session.token, refresh: session.refresh_token }),
    );
  } catch {
    /* Private mode / storage disabled — a reload will just fail louder. */
  }
}

export function restoreSession(partnerSlug: string): Session | null {
  try {
    const raw = window.sessionStorage.getItem(cacheKey(partnerSlug));
    if (!raw) return null;
    const parsed = safeParse(raw);
    const token = readString(parsed, 'token');
    const refresh = readString(parsed, 'refresh');
    if (!token || !refresh) return null;
    const session = Session.restore(token, refresh);
    return session.isexpired(Date.now() / 1000) ? null : session;
  } catch {
    return null;
  }
}

export function clearCachedSession(partnerSlug: string): void {
  try {
    window.sessionStorage.removeItem(cacheKey(partnerSlug));
  } catch {
    /* nothing to do */
  }
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readString(source: unknown, key: string): string | null {
  if (typeof source !== 'object' || source === null) return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readBool(source: unknown, key: string): boolean {
  if (typeof source !== 'object' || source === null) return false;
  return (source as Record<string, unknown>)[key] === true;
}
